import React, { useEffect, useState } from "react";
import { ChevronDown, Copy, Check } from "lucide-react";
import api from "../../utils/api";
import { Avatar, Badge, cn } from "../ui";
import {
  CHANNEL_LABELS, channelOf, formatDuration, liveChatOutcomeLabel, liveChatStatusLabel, shortId, toMs, visitorName,
} from "../../utils/liveChat";

const fmt = (value) => {
  const ms = toMs(value);
  if (!ms) return null;
  return new Date(ms).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
};

const Field = ({ label, children }) =>
  children === null || children === undefined || children === "" ? null : (
    <div className="grid grid-cols-[96px_1fr] gap-2 py-1.5 text-xs">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="min-w-0 break-words text-zinc-900">{children}</dd>
    </div>
  );

const Section = ({ title, open, onToggle, children }) => (
  <div className="border-b border-zinc-100">
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between px-4 py-3 text-left">
      <span className="text-xs font-semibold text-zinc-900">{title}</span>
      <ChevronDown className={cn("h-3.5 w-3.5 text-zinc-400 transition-transform", open && "rotate-180")} />
    </button>
    {open && <dl className="px-4 pb-3">{children}</dl>}
  </div>
);

const CopyId = ({ id }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard blocked: nothing to do
    }
  };
  return (
    <button type="button" onClick={copy} title="Copy conversation ID"
      className="inline-flex items-center gap-1 rounded px-1 font-mono text-[11px] text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800">
      #{shortId(id)} {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

const DetailsPanel = ({ session, messages = [], tags = [] }) => {
  const [open, setOpen] = useState({ details: true, conversation: true, params: false, device: false });
  const [contact, setContact] = useState(null);
  const [registeredParams, setRegisteredParams] = useState([]);
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  useEffect(() => {
    api.getWidgetConfig().then((c) => c && c.custom_parameters && setRegisteredParams(c.custom_parameters)).catch(() => {});
  }, []);

  const sessionKey = session ? session.session_id : null;
  useEffect(() => {
    let alive = true;
    setContact(null);
    if (!session) return undefined;
    const searchVal = session.user_email || session.user_id || session.user_phone || session.session_id;
    api.getContacts(1, 5, searchVal)
      .then((res) => alive && setContact(res && res.contacts && res.contacts.length ? res.contacts[0] : null))
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  if (!session) {
    return (
      <aside className="hidden w-[300px] shrink-0 flex-col items-center justify-center rounded-xl border border-zinc-200 bg-white p-6 text-center xl:flex">
        <p className="text-sm font-medium text-zinc-700">Visitor details</p>
        <p className="mt-1 text-xs text-zinc-500">Open a chat to see who you're talking to.</p>
      </aside>
    );
  }

  const name = visitorName(session);
  const email = (contact && contact.email && contact.email !== "N/A" ? contact.email : null) || session.user_email;
  const phone = (contact && contact.phone && contact.phone !== "N/A" ? contact.phone : null) || session.user_phone;
  const contactName = contact && contact.name ? contact.name.replace(/\s*\([a-f0-9-]{36}\)/i, "") : null;
  const params = session.custom_attributes || session.custom_parameters || (contact && contact.custom_parameters) || {};
  const paramEntries = Object.entries(params).filter(
    ([k, v]) => v !== null && v !== undefined && registeredParams.some((p) => p.toLowerCase() === k.toLowerCase())
  );
  const status = liveChatStatusLabel(session.handoff_status);

  return (
    <aside className="hidden w-[300px] shrink-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white xl:flex">
      <div className="flex flex-col items-center border-b border-zinc-100 px-4 pb-4 pt-5 text-center">
        <Avatar name={name} seed={session.session_id} size="lg" />
        <h3 className="mt-2 max-w-full truncate text-sm font-semibold text-zinc-900">{name}</h3>
        {email && <p className="max-w-full truncate text-xs text-zinc-500">{email}</p>}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
          <Badge variant="outline">{CHANNEL_LABELS[channelOf(session)]}</Badge>
          {status && <Badge variant={session.handoff_status === "requested" ? "warning" : ["active", "claimed"].includes(session.handoff_status) ? "success" : "default"}>{status}</Badge>}
          <CopyId id={session.session_id} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <Section title="Details" open={open.details} onToggle={() => toggle("details")}>
          <Field label="Email">{email}</Field>
          <Field label="Phone">{phone}</Field>
          {contactName && contactName !== name && <Field label="Contact">{contactName}</Field>}
          <Field label="Location">{session.location}</Field>
          <Field label="Group">{session.group || session.group_name}</Field>
          <Field label="Priority">{session.priority}</Field>
          <Field label="Agent">{session.assigned_to_name || session.taken_over_by_name || "Not assigned"}</Field>
          {tags.length > 0 && (
            <Field label="Tags">
              <span className="flex flex-wrap gap-1">{tags.map((t) => <Badge key={t} variant="outline">{t}</Badge>)}</span>
            </Field>
          )}
        </Section>

        <Section title="Conversation" open={open.conversation} onToggle={() => toggle("conversation")}>
          <Field label="Started">{fmt(session.created_at)}</Field>
          <Field label="Last activity">{fmt(session.updated_at)}</Field>
          <Field label="Messages">{messages.length || null}</Field>
          {session.handoff_requested_at && <Field label="Asked for agent">{fmt(session.handoff_requested_at)}</Field>}
          {session.wait_seconds != null && <Field label="Waited">{formatDuration(session.wait_seconds)}</Field>}
          {session.handling_seconds != null && <Field label="Handled in">{formatDuration(session.handling_seconds)}</Field>}
          {session.handoff_ended_reason && <Field label="Outcome">{liveChatOutcomeLabel(session.handoff_ended_reason)}</Field>}
          {session.rating ? (
            <Field label="Rating">
              <span className="font-medium">{session.rating} / 5</span>
              {session.rating_comment && <span className="mt-0.5 block text-zinc-500">"{session.rating_comment}"</span>}
            </Field>
          ) : null}
          {session.summary && session.summary !== "New Conversation" && <Field label="First message">{session.summary}</Field>}
        </Section>

        <Section title={`Custom parameters${paramEntries.length ? ` (${paramEntries.length})` : ""}`} open={open.params} onToggle={() => toggle("params")}>
          {paramEntries.length ? paramEntries.map(([k, v]) => (
            <Field key={k} label={k}>
              {typeof v === "object" ? <code className="whitespace-pre-wrap break-all font-mono text-[11px]">{JSON.stringify(v, null, 2)}</code> : String(v)}
            </Field>
          )) : <p className="py-1 text-xs text-zinc-500">None for this visitor.</p>}
        </Section>

        <Section title="Device" open={open.device} onToggle={() => toggle("device")}>
          <Field label="Device">{session.device_type}</Field>
          <Field label="Browser">{session.browser_info}</Field>
          <Field label="System">{session.os_info}</Field>
          {session.ip_address && <Field label="IP address"><span className="font-mono">{session.ip_address}</span></Field>}
          {!session.device_type && !session.browser_info && !session.os_info && !session.ip_address && (
            <p className="py-1 text-xs text-zinc-500">Not available for this channel.</p>
          )}
        </Section>
      </div>
    </aside>
  );
};

export default DetailsPanel;
